import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  FormError,
  Input,
  Label,
  Text,
} from '@psyomics/ui-components';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CancelTarget } from './types';

type CancelReferralRequestConfirmProps = {
  readonly cancelling: boolean;
  readonly failed: boolean;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
};

const CancelReferralRequestConfirm = ({
  cancelling,
  failed,
  onClose,
  onConfirm,
}: CancelReferralRequestConfirmProps) => {
  const { t } = useTranslation();
  const [confirmation, setConfirmation] = useState('');
  const confirmationId = useId();
  const keyword = t('page.professionalReferral.home.cancelDialog.keyword');
  const isConfirmed = confirmation.trim().toLowerCase() === keyword.trim().toLowerCase();

  return (
    <DialogContent showCloseButton={false}>
      <DialogTitle>{t('page.professionalReferral.home.cancelDialog.title')}</DialogTitle>
      <div className="space-y-4">
        <DialogDescription asChild>
          <Text>{t('page.professionalReferral.home.cancelDialog.description')}</Text>
        </DialogDescription>
        <div className="space-y-2">
          <Label htmlFor={confirmationId}>
            {t('page.professionalReferral.home.cancelDialog.confirmationLabel', { keyword })}
          </Label>
          <Input id={confirmationId} onChange={(event) => setConfirmation(event.target.value)} value={confirmation} />
        </div>
        {failed && <FormError error={t('page.professionalReferral.home.cancelDialog.error')} />}
        <div className="flex justify-end gap-2">
          <Button appearance="secondary" onClick={onClose} type="button">
            {t('common.buttons.goBack')}
          </Button>
          <Button disabled={!isConfirmed} loading={cancelling} onClick={onConfirm} type="button">
            {t('common.buttons.continue')}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
};

type CancelReferralRequestDialogProps = {
  readonly cancelling: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (target: CancelTarget) => void;
  readonly target: CancelTarget | null;
};

const CancelReferralRequestDialog = ({ cancelling, onClose, onConfirm, target }: CancelReferralRequestDialogProps) => (
  <Dialog modal onOpenChange={onClose} open={target !== null}>
    {target !== null && (
      <CancelReferralRequestConfirm
        cancelling={cancelling}
        failed={target.failed}
        onClose={onClose}
        onConfirm={() => onConfirm(target)}
      />
    )}
  </Dialog>
);

export { CancelReferralRequestDialog };
